// The one hook a schedule panel mounts: the job's state and its enable/pause/resume/reschedule. The api is
// bound to the session-bound fetch of the identity the panel mounted under. One operation id
// stays with an action until the server answers it, so a retry after an unknown outcome replays the
// original rather than becoming a second activation.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import {
  createAppScheduleApi,
  ScheduleNotActivated,
  type AppScheduleApi,
  type PrivateSetup,
  type ScheduleAction,
  type ScheduleChoice,
  type ScheduleRefusal,
  type ScheduleState,
} from '../schedule-api';
import { readRetained, writeRetained } from './retained-original';

// The ONE retained original of this job's unresolved action: its action, operation key and the endpoint it
// used (the owner's private first enable names the exact selected tuple). A replay always goes to that same
// endpoint with that same body and key, whatever a later read shows: a current-state read is not the original's
// receipt, so it never clears or rewrites it. Only the original's own answer settles it.
// A new cadence travels with its original too (`schedule`): a replay sends the very cron and zone first sent.
type Pending = {
  action: ScheduleAction;
  id: string;
  mode: 'private' | 'published';
  setup?: PrivateSetup;
  schedule?: ScheduleChoice;
};
const text = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const isChoice = (value: unknown): value is ScheduleChoice => {
  const choice = value as ScheduleChoice;
  return text(choice?.cron) && text(choice.timezone);
};
const isPending = (value: unknown): value is Pending => {
  const pending = value as Pending;
  return (
    ['enable', 'pause', 'resume', 'reschedule'].includes(pending?.action) &&
    text(pending.id) &&
    (pending.schedule === undefined || isChoice(pending.schedule)) &&
    (pending.action !== 'reschedule' ||
      (pending.mode === 'published' && pending.schedule !== undefined)) &&
    (pending.mode === 'published' ||
      (pending.mode === 'private' &&
        pending.action === 'enable' &&
        text(pending.setup?.artifactId) &&
        text(pending.setup?.versionId) &&
        text(pending.setup?.revisionId)))
  );
};
/** ok: the retained original (or none); not ok: the store could not be read, so no fresh mutation may start. */
function retained(key?: string): { pending: Pending | null; ok: boolean } {
  if (!key) return { pending: null, ok: true };
  try {
    return { pending: readRetained(key, isPending), ok: true };
  } catch {
    return { pending: null, ok: false };
  }
}
function keep(key: string | undefined, value: Pending | null): boolean {
  if (!key) return true;
  try {
    writeRetained(key, value);
    return true;
  } catch {
    return false;
  }
}

export type ScheduleNotice = null | ScheduleRefusal | 'unknown' | 'storage';

// The original's own endpoint: private first enable, a reschedule, or a published action.
function send(api: AppScheduleApi, original: Pending) {
  if (original.mode === 'private')
    return original.setup && api.enablePrivate
      ? api.enablePrivate(original.setup, original.id, original.schedule)
      : Promise.resolve({ kind: 'unknown' } as const);
  if (original.action === 'reschedule')
    return original.schedule && api.reschedule
      ? api.reschedule(original.schedule, original.id)
      : Promise.resolve({ kind: 'unknown' } as const);
  return api.run(original.action, original.id);
}

export function useAppSchedule(
  appId: string,
  jobId: string,
  explicit?: AppScheduleApi,
  setup?: PrivateSetup,
  /** Where this job's one unresolved original (action + key) is kept across a closed panel; see retained-original. */
  retainKey?: string,
) {
  const bound = useSessionBoundFetch();
  const [own] = useState(() => createAppScheduleApi(appId, jobId, bound));
  const api = explicit ?? own;
  const [state, setState] = useState<ScheduleState | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // No activation yet and nothing published to select one from: the owner's first enable of the private App.
  const [notActivated, setNotActivated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [first] = useState(() => retained(retainKey));
  // A retained original keeps its unknown notice after a reopen; a store that cannot be read blocks anything fresh.
  const [notice, setNotice] = useState<ScheduleNotice>(
    !first.ok ? 'storage' : first.pending ? 'unknown' : null,
  );
  const pending = useRef<Pending | null>(first.pending);
  const [pendingAction, setPendingAction] = useState<ScheduleAction | null>(
    first.pending?.action ?? null,
  );
  const [blocked, setBlocked] = useState(!first.ok);
  const flying = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const refresh = useCallback(async () => {
    try {
      const next = await api.read();
      if (!alive.current) return;
      // A read of the current state is not the original's receipt: it never clears or rewrites the original.
      setState(next);
      setLoadFailed(false);
      setNotActivated(false);
    } catch (error) {
      if (!alive.current) return;
      if (error instanceof ScheduleNotActivated) {
        setNotActivated(true);
        setLoadFailed(false);
      } else setLoadFailed(true);
    }
  }, [api]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const run = useCallback(
    async (
      action: ScheduleAction,
      schedule?: ScheduleChoice,
    ): Promise<'ok' | 'refused' | 'unknown' | 'skipped'> => {
      if (flying.current || blocked) return 'skipped';
      let original = pending.current;
      if (!original) {
        // A fresh original: its endpoint and body are fixed now and replayed as they are.
        const privateFirst = notActivated && action === 'enable' && setup && api.enablePrivate;
        original = {
          action,
          id: globalThis.crypto.randomUUID(),
          mode: privateFirst ? 'private' : 'published',
          ...(privateFirst ? { setup } : {}),
          ...(schedule && (privateFirst || action === 'reschedule')
            ? { schedule: { cron: schedule.cron, timezone: schedule.timezone } }
            : {}),
        };
        if (!keep(retainKey, original)) {
          // It cannot be kept for a reopen, so it is not started: no mutation without a retained original.
          setBlocked(true);
          setNotice('storage');
          return 'skipped';
        }
        pending.current = original;
        setPendingAction(original.action);
      }
      flying.current = true;
      setBusy(true);
      setNotice(null);
      const outcome = await send(api, original);
      // Only the original's own answer settles it, even if its panel was closed meanwhile.
      if (outcome.kind !== 'unknown') {
        if (pending.current === original) pending.current = null;
        keep(retainKey, null);
      }
      flying.current = false;
      if (!alive.current) return outcome.kind;
      setBusy(false);
      if (outcome.kind !== 'unknown') setPendingAction(null);
      if (outcome.kind === 'ok') {
        setState(outcome.state);
        setNotActivated(false);
      } else if (outcome.kind === 'refused') {
        setNotice(outcome.reason);
        void refresh();
      } else {
        // Unknown: it may have committed. Keep the original and allow the same replay.
        setNotice('unknown');
        void refresh();
      }
      return outcome.kind;
    },
    [api, refresh, notActivated, setup, retainKey, blocked],
  );
  return {
    state,
    notActivated,
    pendingAction,
    canEnablePrivate: Boolean(setup && api.enablePrivate),
    canReschedule: Boolean(api.reschedule),
    loadFailed,
    busy,
    notice,
    /** The private first enable may name the owner's own cadence instead of the declared one. */
    enable: (schedule?: ScheduleChoice) => run('enable', schedule),
    pause: () => run('pause'),
    resume: () => run('resume'),
    reschedule: (schedule: ScheduleChoice) => run('reschedule', schedule),
    /** The unresolved original, replayed as itself (its own action, key and body). */
    retry: () =>
      pending.current ? run(pending.current.action) : Promise.resolve('skipped' as const),
    refresh,
  };
}
